import Dexie from 'dexie';

export const db = new Dexie('NovaCRM_OfflineDB');

db.version(1).stores({
  pendingActions:
    '++id, type, endpoint, method, payload, createdAt, status, retries',
  cachedCatalogs: 'key, data, updatedAt, expiresAt',
  offlineAttendance:
    '++id, employee_id, type, timestamp, latitude, longitude, synced',
  userSession: 'key, value, updatedAt',
});

export const PendingActionStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  FAILED: 'failed',
  COMPLETED: 'completed',
};

export const offlineService = {
  async addPendingAction(action) {
    const record = {
      type: action.type,
      transport: action.transport || 'http',
      endpoint: action.endpoint || null,
      method: action.method || 'POST',
      rpc: action.rpc || null,
      payload: JSON.stringify(action.payload),
      createdAt: new Date().toISOString(),
      status: PendingActionStatus.PENDING,
      retries: 0,
    };

    const id = await db.pendingActions.add(record);
    console.log('[Offline] Action queued:', id, action.type);
    return id;
  },

  async getPendingActions() {
    return await db.pendingActions
      .where('status')
      .anyOf([PendingActionStatus.PENDING, PendingActionStatus.FAILED])
      .toArray();
  },

  async getPendingCount() {
    return await db.pendingActions
      .where('status')
      .anyOf([PendingActionStatus.PENDING, PendingActionStatus.FAILED])
      .count();
  },

  async updateActionStatus(id, status, error = null) {
    const updates = { status };
    if (error) {
      updates.lastError = error;
      const action = await db.pendingActions.get(id);
      if (action) {
        updates.retries = (action.retries || 0) + 1;
      }
    }
    await db.pendingActions.update(id, updates);
  },

  async removeAction(id) {
    await db.pendingActions.delete(id);
  },

  async clearCompletedActions() {
    await db.pendingActions
      .where('status')
      .equals(PendingActionStatus.COMPLETED)
      .delete();
  },

  async cacheData(key, data, ttlMinutes = 60) {
    const now = new Date();
    const expiresAt = new Date(now.getTime() + ttlMinutes * 60 * 1000);

    await db.cachedCatalogs.put({
      key,
      data: JSON.stringify(data),
      updatedAt: now.toISOString(),
      expiresAt: expiresAt.toISOString(),
    });
  },

  async getCachedData(key) {
    const record = await db.cachedCatalogs.get(key);
    if (!record) return null;

    const now = new Date();
    const expiresAt = new Date(record.expiresAt);

    if (now > expiresAt) {
      await db.cachedCatalogs.delete(key);
      return null;
    }

    try {
      return JSON.parse(record.data);
    } catch {
      return null;
    }
  },

  async saveOfflineAttendance(attendance) {
    const record = {
      employee_id: attendance.employee_id,
      type: attendance.type,
      timestamp: attendance.timestamp || new Date().toISOString(),
      latitude: attendance.latitude,
      longitude: attendance.longitude,
      synced: false,
    };

    const id = await db.offlineAttendance.add(record);
    console.log('[Offline] Attendance saved locally:', id);
    return id;
  },

  async getUnsyncedAttendance() {
    return await db.offlineAttendance.where('synced').equals(false).toArray();
  },

  async markAttendanceSynced(id) {
    await db.offlineAttendance.update(id, { synced: true });
  },

  async saveUserSession(key, value) {
    await db.userSession.put({
      key,
      value: JSON.stringify(value),
      updatedAt: new Date().toISOString(),
    });
  },

  async getUserSession(key) {
    const record = await db.userSession.get(key);
    if (!record) return null;

    try {
      return JSON.parse(record.value);
    } catch {
      return null;
    }
  },

  async clearUserSession() {
    await db.userSession.clear();
  },
};

export default offlineService;
